import React, { useCallback, useState } from 'react'
import { AppShell } from '../layout/AppShell'
import { useForm } from 'react-hook-form'
import Input from '../../components/Form/Input'
import { graphql } from '../../gql'
import { useMutation, useQuery } from 'urql'
import { ScanEventForScanHistory, ScanHistoryTable } from './ScanHistoryTable'

import returnIllustration from '../../assets/images/returnIllustration.svg'
import Button from '../../components/Buttons/Button'
import { ArrowRightIcon } from '@heroicons/react/24/solid'
import classNames from 'classnames'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { format } from 'date-fns'
import DestructiveAlertModal from '../../components/Modals/DestructiveAlertModal'
import { useBusinessLocation } from '../../context/BusinessLocationContext'
import { RetireParcelModal } from '../bags/RetireParcelModal'

interface ScanForm {
  serialNumber: string
}

const CreateScanEventMutation = graphql(`
  mutation RegisterScanEvent($bagId: String!, $locationId: ID!) {
    warehouseCreateScanEvent(bagId: $bagId, locationId: $locationId) {
      scanEvent {
        id
        rawInputData
        bag {
          id
          bagId
          cycles
          humanReadableId
        }
        createdAt
      }
    }
  }
`)

const ScanHistoryQuery = graphql(`
    query ScanHistory($locationId: ID, $limit: Int) {
        scanEvents(locationId: $locationId, limit: $limit, orderBy: "-created_at") {
            id
            createdAt
            rawInputData
            bag {
                id
                bagId
                humanReadableId
                cycles
            }
        }
    }
`)

const DeleteScanEventMutation = graphql(`
  mutation DeleteScan($id: ID!) {
    deleteScanEvent(id: $id) {
      found
      deletedId
    }
  }
`)

interface RecentlyRegisteredData {
  serialNumber: string
  createdAt: string
  cycles: number | string
}

export const Scan: React.FC = () => {
  const { register, handleSubmit, reset } = useForm<ScanForm>()
  const [recentlyRegistered, setRecentlyRegistered] =
    useState<RecentlyRegisteredData | null>(null)
  const [error, setError] = useState(false)
  const [confirmScanDeletionForId, setConfirmScanDeletionForId] = useState<
    string | null
  >(null)
  const [confirmParcelRetirementForId, setConfirmParcelRetirementForId] =
    useState<string | null>(null)

  const { selectedLocation } = useBusinessLocation()

  const [{ data }, refetchScanHistory] = useQuery({
    query: ScanHistoryQuery,
    variables: { limit: 5, locationId: selectedLocation?.id },
  })

  const [, createScanEvent] = useMutation(CreateScanEventMutation)
  const [, deleteScanEvent] = useMutation(DeleteScanEventMutation)

  const onSubmit = useCallback(
    async (data: ScanForm) => {
      if (!selectedLocation) {
        console.error('No location selected, cannot register scan')
        return
      }

      const result = await createScanEvent({
        bagId: data.serialNumber,
        locationId: selectedLocation.id,
      })

      setError(false)

      if (result.error) {
        setError(true)
        console.error(result.error)
        return
      }

      // Reset the form
      reset()
      refetchScanHistory({ requestPolicy: 'network-only' })

      const bag = result.data?.warehouseCreateScanEvent?.scanEvent?.bag

      setRecentlyRegistered({
        serialNumber: bag
          ? bag.humanReadableId ?? bag.bagId
          : data.serialNumber,
        createdAt: new Date().toISOString(),
        cycles: bag ? bag.cycles ?? 0 : 'N/A',
      })

      await new Promise(resolve => setTimeout(resolve, 4000))

      setRecentlyRegistered(null)
    },
    [createScanEvent, reset, refetchScanHistory, selectedLocation]
  )

  const handleDeleteScanEvent = useCallback(
    async (id: string) => {
      setConfirmScanDeletionForId(null)
      const result = await deleteScanEvent({ id })

      if (result.error) {
        return
      }

      // Reset the form
      reset()

      refetchScanHistory({ requestPolicy: 'network-only' })
    },
    [deleteScanEvent, refetchScanHistory, reset]
  )

  const events =
    (data?.scanEvents?.filter(
      event => event !== null
    ) as ScanEventForScanHistory[]) ?? []

  return (
    <AppShell>
      <div className={'max-w-[552px] mx-auto flex flex-col gap-6 md:px-0 px-4'}>
        <h1 className={'text-3xl leading-9 font-bold mb-4'}>Scan</h1>
        <form onSubmit={handleSubmit(onSubmit)} className={'flex gap-2 w-full'}>
          <Input
            register={register}
            name={'serialNumber'}
            className={'h-12'}
            placeholder={'Parcel ID'}
            autoFocus={true}
          />
          <Button className={'px-4 bg-emerald-500'} type={'submit'}>
            <ArrowRightIcon className={'h-4 w-4'} />
          </Button>
        </form>
        <div
          id="illustration"
          className={classNames(
            'flex p-4 justify-center items-center flex-col gap-4 border-gray-200 border rounded-md h-[275px]',
            {
              'bg-emerald-50': recentlyRegistered,
              'bg-white': !recentlyRegistered,
              'bg-yellow-50': error,
            }
          )}
        >
          {error ? (
            <>
              <ExclamationTriangleIcon
                strokeWidth={0.5}
                className={'text-yellow-500'}
              />
              <p className={'text-base leading-6 font-normal text-yellow-900'}>
                <b className={'font-semibold'}>Error:</b> Could not read NFC
              </p>
              <p className={'text-sm leading-5 font-normal text-yellow-900'}>
                Please type the parcel ID in the text field above
              </p>
            </>
          ) : recentlyRegistered ? (
            <>
              <CheckCircleIcon
                width={'143px'}
                height={'143px'}
                strokeWidth={0.5}
                className={'text-emerald-500'}
              />
              <p className={'text-emerald-900'}>
                Successfully registered <b>{recentlyRegistered.serialNumber}</b>
              </p>
              <p className={'text-sm leading-5 text-emerald-900 flex gap-1'}>
                <span>
                  {format(
                    new Date(recentlyRegistered.createdAt),
                    'ccc dd MMM yyyy HH:mm:ss'
                  )}
                </span>
                <span>•</span>
                <span>{recentlyRegistered.cycles} cycles</span>
              </p>
            </>
          ) : (
            <>
              <img src={returnIllustration} alt="Return illustration" />
              <p className={'text-gray-600 leading-6 text-center'}>
                Scan or type serial number to register a parcel
              </p>
            </>
          )}
        </div>
        <div className={'border-gray-200 border rounded-md py-6 bg-white'}>
          <ScanHistoryTable
            events={events}
            onDelete={id => setConfirmScanDeletionForId(id)}
            onRetire={id => setConfirmParcelRetirementForId(id)}
          />
        </div>
      </div>
      <RetireParcelModal
        parcelId={confirmParcelRetirementForId}
        isOpen={confirmParcelRetirementForId !== null}
        onClose={() => setConfirmParcelRetirementForId(null)}
        onParcelDeleted={() => setConfirmParcelRetirementForId(null)}
      />
      <DestructiveAlertModal
        isOpen={confirmScanDeletionForId !== null}
        onDestructiveAction={() =>
          handleDeleteScanEvent(confirmScanDeletionForId!)
        }
        onDismiss={() => setConfirmScanDeletionForId(null)}
        title={'Confirm scan deletion'}
        description={
          'Please be sure you want to delete this can before proceeding.'
        }
        dismissButtonText={'Cancel'}
        destructiveButtonText={'Delete scan'}
      />
    </AppShell>
  )
}
